<!-- Root page for landing pages and survey initialization -->
<script lang="ts">
  import { routes, withPreviewMode } from '$lib/routes';
  import { onMount } from 'svelte';
  import { Screen, LandingPage, defaultTheme } from '@walku/core';
  import type { ThemeConfig } from '@walku/core';
  import type { FontFamily } from '@walku/core';
  import type { InterpretedSurvey } from '$lib/utils/surveyInterpreter';
  import type { PageData } from './$types';
  import { goto, beforeNavigate, afterNavigate } from '$app/navigation';
  import { navigating } from '$app/stores';
  import { interpretSurvey } from '$lib/utils/surveyInterpreter';
  import { SurveyController } from '$lib/controllers/surveyController';
  import { surveyStore } from '$lib/stores/surveyStore';
  import { weightsStore } from '$lib/stores/weightsStore';
  import ThemeFrame from '$lib/components/ThemeFrame.svelte';
  import { queryParamService } from '$lib/services/QueryParamService';
  import { setApiBaseUrl } from '$lib/utils/apiConfig';
  import { browser } from '$app/environment';
  import { tokenService } from '$lib/services/TokenService';
  import { geolocationService } from '$lib/services/GeolocationService';

  export let data: PageData;
  // @ts-ignore: InterpretedSurvey should be compatible with Survey
  // Make surveyData reactive to data changes for client-side navigation
  $: surveyData = interpretSurvey(data.survey);
  $: currentScreen = surveyData.screens[0];
  let _isFullscreen = false;
  let isTransitioning = false;
  let shouldDismantle = false;

  // Use the imported defaultTheme from @walku/core
  $: theme = surveyData?.theme || defaultTheme;
  $: documentTitle = currentScreen?.header?.title || 'Welcome';

  // Set API URL from available environment variables
  if (browser) {
    // Set API URL in priority order
    if (import.meta.env.FUNCTION_URL) {
      setApiBaseUrl(import.meta.env.FUNCTION_URL);
    } else if (import.meta.env.VITE_FUNCTION_URL) {
      setApiBaseUrl(import.meta.env.VITE_FUNCTION_URL);
    } else if (import.meta.env.API_ENDPOINT) {
      setApiBaseUrl(import.meta.env.API_ENDPOINT);
    } else if (import.meta.env.VITE_API_ENDPOINT) {
      setApiBaseUrl(import.meta.env.VITE_API_ENDPOINT);
    } else if (window.__API_CONFIG__?.baseUrl) {
      setApiBaseUrl(window.__API_CONFIG__.baseUrl);
    }
  }

  // Helper function to safely extract questionId from screen objects
  // Ensures consistency with the dynamic route
  function getQuestionId(screen: any, fallbackIndex: number = 0): string {
    // First priority: Use explicit questionId if available
    if (screen && typeof screen === 'object' && 'questionId' in screen) {
      return screen.questionId;
    }
    
    // Second priority: For screens, use title as ID if available
    if (screen?.type === 'screen' && screen?.header?.title) {
      return screen.header.title;
    }
    
    // Last resort: Use the index
    return `${fallbackIndex}`;
  }

  // Initialize the survey
  onMount(async () => {
    if (browser && data.survey) {
      // Check if tracking is completely disabled
      const disableTracking = data.survey?.config?.disableTracking === true;
      
      // Check if consent is required
      const requiresConsent = data.survey?.config?.consentPolicy;
      
      // If tracking is disabled, don't initialize any stores or tokens
      if (disableTracking) {
        return;
      }
      
      // If consent is required, check consent status before initializing
      if (requiresConsent) {
        const { surveyInitializer } = await import('@lib/services/SurveyInitializer');
        
        if (!surveyInitializer.getConsentStatus()) {
          console.log('Consent required but not granted - deferring initialization');
          // Don't initialize anything - let the consent banner handle it
          return;
        }
      }
      
      // Check for autoSkip configuration first
      if (currentScreen?.config?.autoSkip) {
        // For autoSkip, we need to initialize the survey store first
        // before redirecting to ensure the dynamic route has access to survey config
        try {
          await SurveyController.initializeSurveyFromRawData(data.survey);
        } catch (error) {
          console.error('Failed to initialize survey with token:', error);
          // Continue anyway - the layout will handle showing the error banner
        }

        // Update the store to allow navigation to the next question
        surveyStore.setCurrentQuestion(1);

        // Prepare the navigation path and preserve preview mode parameters
        const isPreviewMode = queryParamService.isQueryParamTrue('preview');
        const path = withPreviewMode(routes.survey.question(1), isPreviewMode);
        // Always set preview mode based on the current query parameter state
        surveyStore.setPreviewMode(isPreviewMode);

        // Now redirect to the dynamic route
        await goto(path);
        return;
      }

      // Initialize the survey using the abstracted method
      let surveyId;
      try {
        const result = await SurveyController.initializeSurveyFromRawData(data.survey);
        surveyId = result.surveyId;
      } catch (error) {
        console.error('Failed to initialize survey with token:', error);
        // Continue anyway - the layout will handle showing the error banner
        surveyId = data.survey?.config?.id;
      }
      
      // Set the current question index to 0 (for root page)
      if (surveyId) {
        surveyStore.setCurrentQuestion(0);
      }
      
      // Initialize weightsStore if needed
      // Support both profiles (legacy) and landingPages (new format)
      const profilesOrLandingPages = surveyData?.profiles || surveyData?.landingPages;
      if (surveyData?.axes && profilesOrLandingPages) {
        weightsStore.initialize(surveyData.axes, profilesOrLandingPages);
      }
      
      // Use the abstracted tracking method for the first screen
      if (surveyData?.screens?.length > 0) {
        const screenId = SurveyController.handleScreenTracking(surveyData.screens[0], 0, getQuestionId);
        
        // No need to force submit here - we'll only send submission when user actually clicks
        // We're already tracking the view with handleScreenTracking
      }
      
      // Check if geolocation is needed on page load and trigger it if so
      if (currentScreen?.config?.actions?.getGoogleGeolocation?.onPageLoad) {
        // Get geolocation silently (direct call instead of event due to onMount context)
        const failSilently = currentScreen.config.actions.getGoogleGeolocation.failSilently || false;
        geolocationService.getGeolocation({ failSilently });
      }
    }
  });

  // Handle screen clicks
  async function handleScreenClick(screen: Screen, index: number) {
    if (isTransitioning) return;
    
    // Get the screen ID using the consistent helper
    const screenId = getQuestionId(screen, index);
    
    // If it's a screen, track completion with 'submit' action
    // This will trigger the API submission automatically
    if (screen.type === 'screen') {
      // Use trackCompletion with 'submit' action type to send to API 
      SurveyController.trackCompletion(
        screenId,
        'screen',
        true, // Boolean for screen interactions
        'submit', // Use explicit 'submit' action type to trigger API call
        index
      );
    }

    // If it's the last screen and there's a success URL, redirect to it
    if (index === surveyData.screens.length - 1 && surveyData.successUrl) {
      window.location.href = surveyData.successUrl;
      return;
    }
    
    // Start dismantling animation
    shouldDismantle = true;
    isTransitioning = true;

    // Wait for the dismantling animation
    await new Promise((resolve) => setTimeout(resolve, 550));
    
    // Check if the survey is completed and reinitialize if needed
    // This allows users to restart the survey after completion
    let currentStatus = null;
    const unsubscribe = surveyStore.subscribe(state => {
      currentStatus = state?.status;
    });
    unsubscribe();
    
    if (currentStatus === 'completed') {
      // Reinitialize the survey to clear the completed state
      // This will set status to 'not-started' and allow navigation to /1
      try {
        await SurveyController.initializeSurveyFromRawData(data.survey);
      } catch (error) {
        console.error('Failed to reinitialize survey:', error);
        // Continue anyway - navigation can still work
      }
    }
    
    // When clicking from the root page to first question,
    // ensure the highest index is set to 1 to allow navigation
    surveyStore.setCurrentQuestion(1);

    // Prepare the navigation path and preserve preview mode parameters
    const isPreviewMode = queryParamService.isQueryParamTrue('preview');
    const path = withPreviewMode(routes.survey.question(index + 1), isPreviewMode);

    // Otherwise redirect to the dynamic route for the next question
    await goto(path, {
      replaceState: false,
      noScroll: true,
      keepFocus: true,
      invalidateAll: false,
    });
  }
  
  /**
   * Handle geolocation request from the Screen component
   * @param event - The event that triggered the geolocation request
   */
  async function handleGeolocation(event: CustomEvent) {
    if (!browser) return;
    
    try {
      // Get the failSilently option from the current screen config
      const failSilently = currentScreen?.config?.actions?.getGoogleGeolocation?.failSilently || false;
      
      // Call the geolocation service
      await geolocationService.getGeolocation({ failSilently });
    } catch (error) {
      // Always log configuration errors like missing API key
      // if (error instanceof Error && error.message.includes('No Google Geolocation API key')) {
        // console.error('[Root Page] Missing Google Geolocation API key:', error);
      // } else {
        // For runtime errors, we rely on the service to handle based on failSilently
        // console.error('[Root Page] Geolocation error:', error);
      // }
    }
  }

  function handleFullscreenRequest() {
    _isFullscreen = true;
  }
</script>

<ThemeFrame theme={theme} {documentTitle} fullscreen={_isFullscreen}>
  <div class="screen-wrapper">
    {#if currentScreen.type === 'landingPage'}
      <LandingPage
        header={currentScreen.header}
        cta={currentScreen.cta}
        config={currentScreen.config}
        blocks={currentScreen.blocks}
        {shouldDismantle}
        on:click={() => handleScreenClick(currentScreen, 0)}
        on:getGeolocation={handleGeolocation}
        on:requestFullscreen={handleFullscreenRequest}
      />
    {:else}
      <Screen
        header={currentScreen.header}
        cta={currentScreen.cta}
        config={currentScreen.config}
        blocks={currentScreen.blocks}
        on:click={() => handleScreenClick(currentScreen, 0)}
        on:getGeolocation={handleGeolocation}
      />
    {/if}
  </div>
</ThemeFrame>

<style>
  .screen-wrapper {
    width: 100%;
    height: 100%;
    display: flex;
  }
</style>
